import { useState, useEffect, useCallback } from 'react';
import {
  getAffaireActiveStatus,
  setAffaireInactiveTemporary,
  setAffairePausedUntilActivity,
  clearAffaireManualInactive,
  refreshAffaireActiveStatus,
} from '../api/affaireActive.api';
import type {
  AffaireActiveStatus,
  SetInactiveTemporaryRequest,
  SetInactivePausedRequest,
} from '../types/tracker.types';

export const useAffaireActiveStatus = (affaireId: number | null | undefined) => {
  const [status, setStatus] = useState<AffaireActiveStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!affaireId) {
      setStatus(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await getAffaireActiveStatus(affaireId);
      if (response.success) {
        setStatus(response.data);
      } else {
        setError(response.message || 'Failed to fetch status');
      }
    } catch (err) {
      console.error('Error fetching affaire active status:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setStatus(null);
    } finally {
      setIsLoading(false);
    }
  }, [affaireId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const setTemporaryInactive = async (
    request: SetInactiveTemporaryRequest
  ): Promise<boolean> => {
    if (!affaireId) return false;

    try {
      setIsLoading(true);
      setError(null);
      const response = await setAffaireInactiveTemporary(affaireId, request);
      if (response.success) {
        setStatus(response.data);
        return true;
      }
      setError(response.message || 'Failed to set temporary inactive');
      return false;
    } catch (err) {
      console.error('Error setting temporary inactive:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const setPausedUntilActivity = async (
    request: SetInactivePausedRequest
  ): Promise<boolean> => {
    if (!affaireId) return false;

    try {
      setIsLoading(true);
      setError(null);
      const response = await setAffairePausedUntilActivity(affaireId, request);
      if (response.success) {
        setStatus(response.data);
        return true;
      }
      setError(response.message || 'Failed to set paused');
      return false;
    } catch (err) {
      console.error('Error setting paused:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const clearManualInactive = async (): Promise<boolean> => {
    if (!affaireId) return false;

    try {
      setIsLoading(true);
      setError(null);
      const response = await clearAffaireManualInactive(affaireId);
      if (response.success) {
        setStatus(response.data);
        return true;
      }
      setError(response.message || 'Failed to clear manual inactive');
      return false;
    } catch (err) {
      console.error('Error clearing manual inactive:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshStatus = async (): Promise<boolean> => {
    if (!affaireId) return false;

    try {
      setIsLoading(true);
      setError(null);
      const response = await refreshAffaireActiveStatus(affaireId);
      if (response.success) {
        setStatus(response.data);
        return true;
      }
      setError(response.message || 'Failed to refresh status');
      return false;
    } catch (err) {
      console.error('Error refreshing status:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    status,
    isLoading,
    error,
    refetch: fetchStatus,
    setTemporaryInactive,
    setPausedUntilActivity,
    clearManualInactive,
    refreshStatus,
  };
};
